/**
 * Ц6/AD-378. Текст уведомления на языке читателя.
 *
 * Сервер кладёт в строку уведомления готовый русский текст (`title`/`content`)
 * и рядом, в `data`, ключи перевода: `titleKey`, `contentKey` и параметры
 * `contentParams` (`notifications.service.ts`, `buildEventNotificationFields`).
 * Словари под эти ключи на фронте есть на пяти языках, но экраны уведомлений
 * рисовали `notification.title`/`notification.content` как есть — и немец с
 * испанцем читали русскую фразу.
 *
 * Ключ приезжает из `jsonb`, то есть ничем не гарантирован, и строки в базе
 * лежат ещё от прежних версий сервера. Поэтому ключ не подставляется в словарь
 * как есть, а сверяется с перечнем известных: неизвестный ключ, отсутствующий
 * перевод — показываем то, что прислал сервер, а не ключ на экране.
 */

/** Ключи блока `notificationTitle` в словаре `notifications`. */
export const NOTIFICATION_TITLE_KEYS = [
  "event_created",
  "event_updated",
  "event_reminder",
  "event_cancelled",
  "task_assigned",
  "task_updated",
  "task_deadline",
  "task_completed",
  "task_comment",
] as const;

/** Ключи блока `notificationContent` — те же плюс перечисление изменений. */
export const NOTIFICATION_CONTENT_KEYS = [
  ...NOTIFICATION_TITLE_KEYS,
  "event_updated_changed",
] as const;

/** Поля события, которые сервер называет в `data.changedFields` (AD-377). */
export const NOTIFICATION_FIELD_KEYS = [
  "title",
  "description",
  "startDate",
  "endDate",
  "location",
  "status",
] as const;

export type NotificationTitleKey = (typeof NOTIFICATION_TITLE_KEYS)[number];
export type NotificationContentKey = (typeof NOTIFICATION_CONTENT_KEYS)[number];
export type NotificationFieldKey = (typeof NOTIFICATION_FIELD_KEYS)[number];

/**
 * Полный ключ словаря. Union, а не `string`: `t` из react-i18next перегружен
 * по словарю пространства имён, и свободная строка там не проходит проверку
 * типов (а на некоторых формах и роняет компилятор — см. `useNotificationText`).
 */
export type NotificationTranslationKey =
  | `notificationTitle.${NotificationTitleKey}`
  | `notificationContent.${NotificationContentKey}`
  | `notificationFields.${NotificationFieldKey}`;

export type NotificationTranslate = (
  key: NotificationTranslationKey,
  options?: Record<string, unknown>,
) => string;

export interface NotificationTextSource {
  title?: string | null;
  content?: string | null;
  data?: Record<string, unknown> | null;
}

export interface NotificationText {
  title: string;
  content: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Сверяет пришедший из `jsonb` ключ с перечнем известных. */
function knownKey<T extends string>(
  value: unknown,
  prefix: string,
  known: readonly T[],
): T | null {
  if (typeof value !== "string" || !value.startsWith(`${prefix}.`)) return null;
  const tail = value.slice(prefix.length + 1);
  return known.find((candidate) => candidate === tail) ?? null;
}

/** Перевод или null, если словарь этого ключа не знает. */
function translated(
  t: NotificationTranslate,
  key: NotificationTranslationKey,
  params?: Record<string, unknown>,
): string | null {
  const value = t(key, params);
  if (typeof value !== "string") return null;
  // i18next без словаря (и без инстанса вовсе) возвращает сам ключ.
  if (value === key || value === `notifications:${key}`) return null;
  return value;
}

/**
 * Список изменённых полей события (AD-377) — словами языка читателя.
 * Если хоть одно название не переведено, возвращается null: лучше показать
 * серверную фразу целиком, чем смесь языков или машинные имена полей.
 */
function changesLabel(
  t: NotificationTranslate,
  params: Record<string, unknown>,
): string | null {
  const raw = params["changedFields"];
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const labels: string[] = [];
  for (const field of raw) {
    const known = knownKey(
      `notificationFields.${String(field)}`,
      "notificationFields",
      NOTIFICATION_FIELD_KEYS,
    );
    if (!known) return null;
    const label = translated(t, `notificationFields.${known}`);
    if (!label) return null;
    labels.push(label);
  }
  return labels.join(", ");
}

export function resolveNotificationText(
  notification: NotificationTextSource,
  t: NotificationTranslate,
): NotificationText {
  const data = asRecord(notification.data);
  const params: Record<string, unknown> = {
    ...asRecord(data["contentParams"]),
  };

  const changes = changesLabel(t, params);
  if (changes) params["changes"] = changes;

  const titleKey = knownKey(
    data["titleKey"],
    "notificationTitle",
    NOTIFICATION_TITLE_KEYS,
  );
  const contentKey = knownKey(
    data["contentKey"],
    "notificationContent",
    NOTIFICATION_CONTENT_KEYS,
  );

  const title =
    (titleKey ? translated(t, `notificationTitle.${titleKey}`) : null) ??
    notification.title ??
    "";

  // Фраза с перечислением изменений без самого перечисления бессмысленна —
  // тогда остаётся серверный текст, который перечисление уже содержит.
  const contentTranslatable =
    contentKey !== null &&
    (contentKey !== "event_updated_changed" || changes !== null);

  const content =
    (contentTranslatable && contentKey
      ? translated(t, `notificationContent.${contentKey}`, params)
      : null) ??
    notification.content ??
    "";

  return { title, content };
}
